import { Component, Inject, OnInit, Input } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Observable, Subject, of } from "rxjs";
import { catchError, filter } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../../shared/dialogs/components/confirmation-dialog.component';
import { MessageDialogComponent } from '../../../../shared/dialogs/components/message-dialog.component';

import { AccesAction, AccesEtat } from '../../../../app.constants';
import { EventBusService, Events } from '../../../../technical-modules/events/services/event-bus.service';
import { JobProgressDisplayCommand } from '../../../../technical-modules/jobs/components/job-progress-dialog';
import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';

import { UserService } from '../../../humain/user/services/user.service';

import { GestionAccesGenericComponentBase } from './gestion-acces-generic.component-base';
import { CoactiviteDecision } from '../entities/coactivite-decision.constants';

import { AccesService } from '../services/acces.service';
import { AccesSearchCriteria } from '../entities/acces-search-criteria';
import { AccesSearchResult } from '../entities/acces-search-result';
import { AccesActionRequest } from '../entities/acces-action-request';
import { AccesTLR } from '../entities/acces-tlr.entity';
import { MassChangeEtatOrder } from '../entities/mass-change-etat-acces-order.entity';
import { MassCloseAccesOrder } from '../entities/mass-close-acces-order.entity';
import { MassDenyAccesOrder } from '../entities/mass-deny-acces-order.entity';

@Component({
	selector: 'gestion-acces-tarif-negatif',
	templateUrl: 'gestion-acces-tarif-negatif.html',
	// styleUrls: ['gestion-liste-acces.css'],
})
export class GestionAccesTarifNegatifComponent extends GestionAccesGenericComponentBase<AccesSearchResult[]> implements OnInit {
	planificationCallSubject$ = new Subject<void>();
	jobProgressDisplayCommands$ = new Subject<JobProgressDisplayCommand>();
	
	private toIds = (acces: AccesSearchResult[]) => acces.map($ => $.id);

	constructor(
		private dialog: MatDialog,
		
		@Inject(EventBusService) eventBusService: EventBusService,
		@Inject(JSR310ConversionService) jsr310Service: JSR310ConversionService,

		@Inject(AccesService) accesService: AccesService,
		@Inject(UserService) userService: UserService,
	) {
		super(eventBusService, jsr310Service, accesService, userService);
		this.title = 'Tarif Négatif';
	}

	protected override loadData(): void {
		this.searchCriteria.onlyTarifNegatifs = true;
		super.loadData();
	}
	
	private checkMonoTarget = (action: AccesActionRequest<AccesSearchResult[]>) => {
		if (action.acces.length > 1) {
			throw new Error("Cette operation n'est prevue que pour fonctionner avec un seul acces");
		}
	};
	
	// Adapteur pour les actions des listes sans selection multiple
	onActionRequestSingle(request: AccesActionRequest<AccesSearchResult>) {
		let newRequest = new AccesActionRequest<AccesSearchResult[]>();
		newRequest.acces = [request.acces];
		newRequest.etat = request.etat;
		newRequest.mode = request.mode;
		newRequest.request = request.request;
		this.onActionRequest(newRequest);
	}

	override onActionRequest(accesActionRequest: AccesActionRequest<AccesSearchResult[]>) {
		if (accesActionRequest.request === AccesAction.EXPORTER) {
			this.exporter(accesActionRequest.etat);
		} else if ( new Set(accesActionRequest.acces.map($ => $.jobExecutionId)).size === 1 ) {
			// pas de melange de batch
			switch (accesActionRequest.request) {
				case AccesAction.ASSOCIER_ARRETS:
					this.checkMonoTarget(accesActionRequest);
					this.displayAssociationArretsCommands.next([accesActionRequest.acces[0].id]);
					break;
				case AccesAction.ASSOCIER_OPERATIONS:
					this.checkMonoTarget(accesActionRequest);
					this.eventBusService.emit(Events.AssociationOperationsModalRequested, accesActionRequest.acces[0].id);
					break;
				case AccesAction.AUTORISER:
					this.authorize(accesActionRequest.acces);
					break;
				case AccesAction.DEMANDE_VALIDATION:
					this.demanderValidation(this.toIds(accesActionRequest.acces));
					break;
				case AccesAction.DUPLIQUER:
					this.checkMonoTarget(accesActionRequest);
					this.eventBusService.emit(Events.DuplicateAccesModalRequested, accesActionRequest.acces[0].id);
					break;
				case AccesAction.FERMER:
					if (accesActionRequest.mode === 'mass') {
						this.fermerMultiAcces(accesActionRequest.acces);
					} else {
						this.checkMonoTarget(accesActionRequest);
						this.displayAccesFermetureCommands.next(accesActionRequest.acces[0].id);
					}
					break;
				case AccesAction.MODIFIER:
					// Dans le cas particulier de ce panneau, on a que des acces de tarifs negatif
					// Il faut donc aiguiller automatiquement les demandes de modifications sur
					// le panne specifique dedie a la modification d'un tarif negatif
					this.modificationTarifNegatifCallSubject$.next(this.toIds(accesActionRequest.acces));
					break;
				case AccesAction.REFUSER:
					this.displayRefusAccesCommands.next(this.toIds(accesActionRequest.acces));				
					break;
				case AccesAction.SUPPRIMER:
					this.supprimerMultiAcces(this.toIds(accesActionRequest.acces));
					break;
				default:
					// rien à faire ici
			}
		}
	}
	
	private displayJobProgression(launchResult: Observable<number>, title: string): Observable<any> {
		let dialogClosedSubject$ = new Subject<void>();

		launchResult.pipe(
			catchError(
				(response: HttpErrorResponse) => {
					if (response.error && response.error.message) {
						alert(response.error.message);
					} else {
						alert("Erreur lors de: " + title + "(" + response.status + ")");
					}
					throw new Error();
				}
			)
		).subscribe(
			(jobExecutionId) => {
				this.jobProgressDisplayCommands$.next({
					title: title,
					jobExecutionId: jobExecutionId,
					dialogClosedSubject: dialogClosedSubject$,
				});
			}
		);
		
		return dialogClosedSubject$;
	}
	
	private accesToCheck!: AccesSearchResult[];
	private accesToValid!: number[];
	private coactifAccesIds!: number[];
	private accesToDeny!: number[];
	private accesToDisplayAsDenied!: AccesSearchResult[];
	
	private authorize(accesList: AccesSearchResult[]) {
		this.accesToCheck = accesList;
		this.accesToValid = [];
		this.coactifAccesIds = [];
		this.accesToDeny = [];
		this.accesToDisplayAsDenied = [];
		this.callCoactiviteChecker();
	}
	
	private callCoactiviteChecker() {
		let acces = this.accesToCheck[0];
		
		// confier la suite des operations au checker adapté
		if (acces.parcEnergie === 'Solaire') {
			this.checkCoactiviteSolaireCommands.next({
				accesId: acces.id,
				ouvragesIds: acces.ouvrageIds.split(",").map($ => +$),
				isUserCac: this.user.id === acces.cacId,
				isUserCc: this.user.id === acces.ccId && acces.typeAcces === 'A_DISTANCE'
			});
		} else if (acces.parcEnergie === 'Eolienne') {
			// un acces EOL est mono ouvrage
			this.checkCoactiviteEolienCommands.next({
				accesId: acces.id,
				accesCegex: acces.accesCegex,
				ouvrageId: +acces.ouvrageIds.split(",")[0],
				isUserCac: this.user.id === acces.cacId,
				isUserCc: this.user.id === acces.ccId && acces.typeAcces === 'A_DISTANCE'
			});
		}				
	}
		
	onCoactiviteDecision(_accesId: number | null, decision: CoactiviteDecision) {
		// Retirer l'acces de la pile a traiter
		let [acces] = this.accesToCheck.splice(0,1);

		// Placer l'acces dans la bonne pile de traitement		
		if (decision === CoactiviteDecision.AUTHORIZE) {
			this.accesToValid.push(acces.id);
		} else if (decision === CoactiviteDecision.DENY){
			this.accesToDeny.push(acces.id);
			if (acces.parcEnergie === 'Eolienne') {
				this.accesToDisplayAsDenied.push(acces);
			}
		} else if (decision === CoactiviteDecision.ASK_VALIDATION) {
			this.demanderValidation([acces.id]);
		} else if (decision === CoactiviteDecision.AUTHORIZE_WITH_FORCE) {
			this.accesToValid.push(acces.id);
			this.coactifAccesIds.push(acces.id);
		}
		
		// Deux cas: on a fini la pile, ou pas
		if (this.accesToCheck.length > 0) {
			// La pile n'est pas termine, on relance le check avec l'acces suivant
			this.callCoactiviteChecker();
			return;
		}
		
		// Si la pile est terminee, on execute tous les enregistrements
		this.onCompletionCoactiveChecks(this.accesToValid, this.accesToDeny, this.coactifAccesIds, 4);
	}
	
	protected onCompletionCoactiveChecks(accesToAuthorizeIds: number[], accesToDenyIds: number[], accesToForceIds: number[], motifId: number) {
		this.persistAccesAutorizations(accesToAuthorizeIds, accesToForceIds).subscribe(
			() => {
				this.persistAccesDenials(accesToDenyIds, motifId).subscribe(
					() => {
						this.callAccesListRefresh();
						if (this.accesToDisplayAsDenied.length > 0) {
							let messages = this.accesToDisplayAsDenied.map(
								acces => acces.parcCode + " - " + acces.numero
							);
							this.dialog.open(MessageDialogComponent, {
								width: '400px',
								data: {
									title: "Liste des accès éolien refusés pour coactivité",
									messages: messages
								}
							});
						}
					}
				)
			}
		);
	}
		
	private persistAccesAutorizations(accesIds: number[], accesToForceIds: number[]): Observable<any> {
		// En fonction du nombre d'acces a authoriser
		if (accesIds.length === 0 && accesToForceIds.length === 0) {
			// Aucun acces, renvoyer directement le signal de completion
			return of(0);
		} else {
			let command = {
				accesIds: accesIds,
				etatId: AccesEtat.EN_COURS,
				date: this.jsr310Service.toLocalDateTime(new Date()),
				coactifIds: accesToForceIds
			} as MassChangeEtatOrder;
			
			// S'il y en a plusieurs, appeler le traitement en batch
			return this.displayJobProgression(
				this.accesService.launchChangeEtatAccesJob(command),
				"Autorisation des accès",
			);
		}
	}
	
	private persistAccesDenials(accesIds: number[], motifId: number): Observable<any> {
		// En fonction du nombre d'acces a refuser
		if (accesIds.length === 0) {
			// Aucun acces, renvoyer directement le signal de completion
			return of(0);
		} else if (accesIds.length ===1) {
			// S'il n'y en a qu'un, appeler l'implementation de base
			return super.persistAccesDenial(accesIds[0], motifId);
		} else {
			// S'il y en a plusieurs, appeler le traitement en batch
			let order = {
				accesIds: accesIds,
				motifId: motifId,
				dateFermeture: this.jsr310Service.toLocalDateTime(new Date()),
			} as MassDenyAccesOrder;
		
			// Appeler le serveur pour lancer le batch
			return this.displayJobProgression(
				this.accesService.launchDenyAccesJob(order),
				"Refus des accès",
			);
		}
	}

	private demanderValidation(accesIds: number[]) {
		if (accesIds.length === 1) {
			this.accesService.demanderValidation(accesIds[0]).pipe(
				catchError(
					(response: HttpErrorResponse) => {
						if (response.error && response.error.message) {
							alert(response.error.message);
						} else {
							alert("Erreur lors de la demande de validation de cet accès (" + response.status + ")");
						}
						throw new Error();
					}
				)
			).subscribe(
				(_resultat: any) => {
					this.callAccesListRefresh();
				}
			);

			return;
		}

		// Appeler le serveur pour lancer le batch
		this.displayJobProgression(
			this.accesService.launchAskValidationAccesJob(accesIds),
			"Demande de validation des accès",
		).subscribe(
			this.callAccesListRefresh
		);
	}
	
	override onCompletingRefus(acces: AccesTLR[], motifId: number) {
		if (motifId > -1) {
			let accesIds = acces.map($ => $.id);
			
			this.persistAccesDenials(accesIds, motifId).subscribe(
				this.callAccesListRefresh
			);
		}
	}
	
	private fermerMultiAcces(acces: AccesSearchResult[]) {
		// Evol 56004 - Dans le cas d'une fermeture de masse, il n'y a pas de fenetre de dialogue pour saisir les details de la fermeture
		// A la place, on previent l'utilisateur des consequences et on lui demande de confirmer
		let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '600px',
			data: {
				title: 'Confirmation',
				message: 'Attention, vous souhaitez fermer ces accès en masse. Cela indique que tous les ouvrages sont à l\'état OK. Pour confirmer, cliquer sur OK.',
			}
		});

		dialogRef.afterClosed().pipe(
			filter(accepted => accepted)
		).subscribe(
			// Appeler le serveur pour lancer le batch
			_ => {
				let order = {
					accesIds: this.toIds(acces),
					dateFermeture: this.jsr310Service.toLocalDateTime(new Date()),
				} as MassCloseAccesOrder;

				this.displayJobProgression(
					this.accesService.launchCloseAccesJob(order),
					"Fermeture des accès",
				).subscribe(
					_ => {
						// Filtrer les acces non-eoliens
						let accesEoliens = acces.filter(acces => acces.parcEnergie === 'Eolienne');
						
						if (accesEoliens.length > 0) {
							// Evol 56463 - RG1
							let dialogRef2 = this.dialog.open(ConfirmationDialogComponent, {
								width: '400px',
								data: {
									title: 'Confirmation',
									message: 'Souhaitez vous lier les accès à des arrêts machines maintenant ?',
								}
							});

							dialogRef2.afterClosed().subscribe(
								(accepted) => {
									this.callAccesListRefresh();
									if (accepted) {
										// Appel a la fenetre d'association des arrets
										this.displayAssociationArretsCommands.next(this.toIds(accesEoliens));
									}
								}
							);
						} else {
							// Aucun parc eolien selectionne: on ne propose pas l'association
							// et on rafraichit de facto les listes d'acces
							this.callAccesListRefresh();
						}
					}
				)
			}
		);
	}

	private supprimerMultiAcces(accesIds: number[]) {
		if (accesIds.length === 1) {
			super.supprimerAcces(accesIds[0]);
			return;
		}

		// Appeler le serveur pour lancer le batch
		this.displayJobProgression(
			this.accesService.launchDeleteAccesJob(accesIds),
			"Suppression des accès",
		).subscribe(
			this.callAccesListRefresh
		);
	}

	protected override getExportFileName(_etat: AccesEtat | null): string {
		let fileName = 'export_accès_tarif_négatif.xls';
		return fileName;
	}

	protected override getExportResult(criteria: AccesSearchCriteria): Observable<Blob> {
		return this.accesService.exportForTarifNegatif(criteria);
	}
}